// eslint-disable-next-line no-unused-vars
import angular from 'angular';
import './modal-query-select.scss';

import { EditorView } from '@codemirror/view';
import { KAlgo } from '~/components/code-editor/kalgo';
import { autocompletion, CompletionContext } from '@codemirror/autocomplete';
import APIV5 from '~/models/api';
import { EditorSelection, EditorState } from '@codemirror/state';
import { LanguageSupport } from '@codemirror/language';
import { linter, diagnosticCount, setDiagnosticsEffect } from '@codemirror/lint';
import { makeNodeName } from '~/components/ktable/utils';
// eslint-disable-next-line import/no-cycle
import { defaultDateRange } from '~/components/query/query-rule/query-rule.controller';
import Query from '~/scripts/services/model/query';
import QueryColumn from '~/scripts/services/model/query-column';
import QueryRule from '~/scripts/services/model/query-rule';
import QueryWhere from '~/scripts/services/model/query-where';
import QueryWhereChild from '~/scripts/services/model/query-where-child';
import QueryWhereNode from '~/scripts/services/model/query-where-node';
import { ThemeExtensions } from '@knitiv/code-editor';
import { wordHover } from './modal-query-select/code-editor/hoverTooltip';
import { removeQuotes, selectColumn } from './modal-query-select/code-editor/columnWidget';
import { algoResolve } from './modal-query-select/code-editor/algo';
// import { indentGuidesPlugin } from './modal-query-select/code-editor/indent-guides';
import { indentationGuides } from './modal-query-select/code-editor/indent-guides';

export default class ModalQuerySelectController {
	/** @type {import('angular').translate.ITranslateService} */
	$translate;

	/* @ngInject */
	/**
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').translate.ITranslateService} $translate
	 * @param {import('angular').ui.bootstrap.IModalInstanceService} $uibModalInstance
	 * @param {any} KTranslate
	 * @param {boolean} disabled
	 * @param {QueryColumn} select
	 * @param {Query} query
	 * @param {import('../query-editor.controller').default} editor
	 * @param {'details' | 'path' | 'filters' | 'expression'} tab
	 */
	constructor(
		$scope,
		$timeout,
		$translate,
		$uibModalInstance,
		KTranslate,
		disabled,
		select,
		query,
		editor,
		tab,
	) {
		this.$scope = $scope;
		this.$uibModalInstance = $uibModalInstance;
		this.KTranslate = KTranslate;

		this.$translate = $translate;
		this.$timeout = $timeout;

		this.active = tab;
		this.disabled = disabled;

		this.query = query;

		// noinspection JSUnusedGlobalSymbols
		this.queryEditorCtrl = editor;

		/** @type {QueryColumn} */
		this.select = { ...select };

		this.from = [{
			node: this.select.kid,
			name: this.select.name,
			path: this.select.path,
			type: this.select.type,
		}];

		this.defaultLabel = undefined;

		if (!this.select.kdata) {
			this.select.kdata = {
				date: null,
				label: null,
				format: null,
				where: null,
			};
		}

		if (!this.select.kdata.where) {
			if (this.query.version && this.query.version >= '3.0.0') {
				this.select.kdata.where = new QueryWhereChild();
				this.select.kdata.where.node = new QueryWhereNode({
					kid: this.select.kid,
					linker_kid: this.select.linker_kid,
					name: this.select.name,
					type: this.select.type,
				});
			}
			else {
				this.select.kdata.where = new QueryWhere();
			}
		}

		// Rule in order to use the date filter and variable management
		if (!this.select.kdata.date) {
			this.select.kdata.date = new QueryRule({
				type:  'K_NODE;DATE',
				label: this.$translate.instant('common.date'),
			});
		}

		/** @type {QueryColumn[]} */
		this.columns = Object.values(query.select);

		/** @type {?EditorView} */
		this.editor = null;

		/** @type {boolean} */
		this.isInvalid = false;

		// this.$scope.$watch('$ctrl.rule.value', () => {
		// 	this.select.kdata.date = this.rule.value;
		// }, true);

		(async () => this.setLabel())();
	}

	name() {
		return makeNodeName(this.select, this.$translate);
	}

	/**
		*
		* @param {QueryColumn[]} columns
		* @returns {Function} callback
		*/
	acColumns(columns) {
		/**
			* @param {CompletionContext} context
			* @returns {any} context
			*/
		return context => {
			const word = context.matchBefore(/\w*/);
			if (word && word.from === word.to && !context.explicit) {
				return null;
			}

			/** @type {import('@codemirror/autocomplete').Completion[]} */
			const options = columns.map(s => ({
				label: makeNodeName(s, null),
				detail: s.id,
				type: 'variable',
				info: `Insert a reference to the ${s.name} column`,
				apply: (view, completion, from, to) => {
					const text = `COLREF("${s.id}")`;
					const transaction = view.state.update({
						changes: { from, to, insert: text },
						selection: EditorSelection.cursor(from + text.length),
					});
					view.dispatch(transaction);
				},
			}));

			return {
				from: word?.from,
				options,
			};
		};
	}

	/**
	 * @param {import('@knitiv/api-client-javascript').AlgoGetInfoOutput['result']} result
	 * @returns {any}
	 */
	acFunctions(result) {
		/**
		 * @param {CompletionContext} context
		 * @returns {any}
		 */
		return context => {
			const word = context.matchBefore(/\w*/);
			if (word && word.from === word.to && !context.explicit) {
				return null;
			}

			/** @type {import('@codemirror/autocomplete').Completion[]} */
			const options = Object.entries(result.functions).map(([key, value]) => ({
				label: key,
				type: 'function',
				// @ts-ignore
				info: this.$translate.instant(value.description),
				apply: (view, completion, from, to) => {
					const text = `${key}()`;
					const transaction = view.state.update({
						changes: { from, to, insert: text },
						//                                                     -1 because of the ()
						selection: EditorSelection.cursor(from + text.length - 1),
					});
					view.dispatch(transaction);
				},
			}));

			return {
				from: word?.from,
				options,
			};
		};
	}

	/**
	 * @param {any} date
	 * @returns {boolean}
	 */
	isDateRange(date) {
		return (
			date?.unit !== undefined
			|| (Array.isArray(date) && date.length > 0 && date[0]?.unit !== undefined)
		);
	}

	/**
		* @param {LanguageSupport} lang
		* @param {QueryColumn[]} columns
		* @param {import('@knitiv/api-client-javascript').AlgoGetInfoOutput['result']} infos
		* @returns {any}
		*/
	kalgoLinter(lang, columns, infos) {
		/**
			* @param {EditorView} editor
			* @returns {any}
			*/
		return editor => {
			/** @type {import('@codemirror/lint').Diagnostic[]} */
			const diagnostics = [];

			const myDocument = editor.state.doc.toString();

			lang.language.parser.parse(myDocument).iterate({
				enter: node => {
					// check language can be parsed
					if (node.type.isError) {
						diagnostics.push({
							from: node.from,
							to: node.to,
							severity: 'error',
							message: 'Syntax error',
						});
					}

					// check columns exist
					const text = myDocument.slice(node.from, node.to);

					if (node.type.name === 'FnKeyword' && text === 'COLREF') {
						const realNode = node.node;
						const parameter1Node = realNode.nextSibling; // Param
						const rawColumn = parameter1Node?.firstChild?.firstChild; // String
						const columnName = removeQuotes(myDocument.slice(rawColumn?.from, rawColumn?.to));
						const column = columns.find(c => c.id === columnName);
						if (!column && rawColumn) {
							diagnostics.push({
								from: rawColumn.from,
								to: rawColumn.to,
								severity: 'error',
								message: `La colonne "${columnName}" n'existe pas.`,
							});
						}
					}

					// check argument number
					if (node.type.name === 'FnKeyword') {
						const parentFunction = node.node.parent;

						const functionName = myDocument.slice(node.from, node.to);

						if (functionName === 'SELF' && this.select.virtual === true) {
							diagnostics.push({
								from: node.from,
								to: node.to,
								severity: 'error',
								message: 'La fonction SELF ne peut pas être utilisée avec une colonne virtuelle.',
							});
						}

						let sibling = node.node?.nextSibling;
						let parameterCount = 0;

						while (sibling) {
							if (sibling.name === 'Param') {
								parameterCount += 1;
							}

							sibling = sibling.nextSibling;
						}

						// Resolve @XXX aliases
						const functionParameters = (
							functionName in infos.functions
							&& typeof infos.functions[functionName] === 'string'
								// @ts-ignore
								? infos.functions[functionName.slice(1)].params
								// @ts-ignore
								: infos.functions[functionName].params
						) ?? [];

						const hasVariadic = functionParameters.some((/** @type {any} */ p) => p.variadic);

						if (
							parentFunction
							&& ((functionParameters.length !== parameterCount && !hasVariadic)
							|| (hasVariadic && parameterCount < functionParameters.length - 1))
						) {
							diagnostics.push({
								from: parentFunction.from,
								to: parentFunction.to,
								severity: 'error',
								message: `La fonction ${functionName} attend ${functionParameters.length} paramètres,`
								+ ` mais vous en avez ${parameterCount}.`,
							});
						}
					}
				},
			});

			return diagnostics;
		};
	}

	/**
		*
		* @param {EditorView} editor
		*/
	async onEditorReady(editor) {
		this.editor = editor;

		if (this.editor?.state) {
			const errors = diagnosticCount(this.editor.state);
			this.isInvalid = errors > 0;
		}
		else {
			this.isInvalid = false;
		}
	}

	/**
	 * @returns {Promise<import("@codemirror/state").Extension[]>}
	 */
	async onEditorSetup() {
		/** @type {import("@codemirror/state").Extension[]} */
		const extensions = [];
		const { columns, $translate } = this;

		const acColumns = this.acColumns(columns);

		const fromAPI = await APIV5.instance.algoGetInfo({ module: 'query' }, { id: 'modal-query-select.init' });
		const { result } = fromAPI;

		const resolvedInfos = algoResolve(result);

		const kalgo = KAlgo(resolvedInfos);

		const acFunctions = this.acFunctions(resolvedInfos);
		const myLinter = this.kalgoLinter(kalgo, columns, resolvedInfos);

		const ll = linter(myLinter);

		const listener = EditorState.transactionFilter.of(tr => {
			// On change, disable saving until linter run
			if (tr.docChanged) {
				this.$timeout(() => {
					this.isInvalid = true;
				});
			}

			let wasChanged = false;

			for (const effect of tr.effects) {
				if (effect.is(setDiagnosticsEffect)) {
					const errors = diagnosticCount(tr.state);
					wasChanged = true;

					this.$timeout(() => {
						this.isInvalid = errors > 0;
					});
				}
			}

			// let's assume there is no linter issue
			if (!wasChanged) {
				const errors = diagnosticCount(tr.state);
				this.$timeout(() => {
					this.isInvalid = errors > 0;
				});
			}

			return tr;
		});

		const guides = indentationGuides();

		extensions.push(
			// @ts-ignore
			autocompletion({ override: [acColumns, acFunctions] }),
			wordHover(result, $translate),
			ll,
			kalgo,
			// @ts-ignore
			selectColumn(columns),
			listener,
			...guides,
			ThemeExtensions.oneLight,
		);

		return extensions;
	}

	/**
	 * @param {boolean} rangeFlag
	 */
	setDateRange(rangeFlag) {
		if (rangeFlag) {
			if (!this.select.kdata.date?.value?.unit || this.select.kdata.date?.value?.duration === undefined) {
				this.$timeout(() => {
					if (this.select.kdata.date) {
						this.select.kdata.date.value = defaultDateRange;
					}
				});
			}
		}
		else {
			this.$timeout(() => {
				if (this.select.kdata.date?.value?.unit) {
					this.select.kdata.date.value = undefined;
				}
			});
		}
	}

	async setLabel() {
		// TODO: verifier class_kid présent dans select ??
		if (this.select.class_kid) {
			this.select.name = await this.KTranslate.request(this.$scope, this.select.class_kid);
		}

		if (!this.select.kdata.label) {
			this.select.kdata.label = this.select.name;
			if (this.select.linker_kid !== '0' && this.select.linker_info) {
				this.select.kdata.label += ` :: ${this.select.linker_info.name}`;
			}
			this.defaultLabel = this.select.kdata.label;
		}
	}

	cancel() {
		this.$uibModalInstance.dismiss();
	}

	submit() {
		if (this.select.kdata.label === this.defaultLabel || this.select.kdata.label?.trim() === '') {
			this.select.kdata.label = null;
		}

		if (this.select.kdata.where instanceof QueryWhere && this.select.kdata.where.rules.length === 0) {
			this.select.kdata.where = null;
		}

		this.$uibModalInstance.close(this.select);
	}
}
